import { useEffect, useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Check,
  GripVertical,
  Loader2,
  Pencil,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";
import type { Tier } from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { TierBadge } from "@/components/shared/tier-badge";
import { formatApiError } from "@/lib/format-api-error";
import { cn } from "@/lib/utils";
import { useAssignmentsForProgram } from "./use-assignments-for-program";
import { useDeleteTier } from "./use-delete-tier";
import { useUpdateTier } from "./use-update-tier";
import { useUploadTierBadge } from "./use-upload-tier-badge";

const BADGE_MAX_BYTES = 1024 * 1024; // 1 MB — matches storage service guard.

// Sortable tier column card. Drag handle, TierBadge (Session-5 assignment
// shape with thin adapter from raw Tier row), inline name edit (on-blur via
// useUpdateTier — no debounce, matches per-cell), badge upload (single
// multipart POST via useUploadTierBadge), and delete with non-destructive
// "reassign first" guard when partners are still assigned to this tier.
//
// The guard re-reads cached assignments via useAssignmentsForProgram to
// avoid prompting destructive intent when the worker would refuse anyway.
// Empty cache (no assignments fetched yet on this tab) falls back to a
// destructive confirm; the worker still enforces the actual count.

export function TierColumnHeader({
  tier,
  programId,
  canDrag,
}: {
  tier: Tier;
  programId: string;
  canDrag: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: tier.id, disabled: !canDrag });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tier.name);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const updateTier = useUpdateTier();
  const deleteTier = useDeleteTier();
  const uploadBadge = useUploadTierBadge();
  const assignmentsQuery = useAssignmentsForProgram(programId);

  useEffect(() => {
    setName(tier.name);
  }, [tier.name]);

  const assignedCount = (assignmentsQuery.data ?? []).filter(
    (a) => a.tier_id === tier.id,
  ).length;

  function onSaveName() {
    const trimmed = name.trim();
    if (!trimmed || trimmed === tier.name) {
      setName(tier.name);
      setEditing(false);
      return;
    }
    updateTier.mutate(
      { programId, id: tier.id, patch: { name: trimmed } },
      {
        onSuccess: () => {
          toast.success("Tier renamed");
          setEditing(false);
        },
        onError: (err) => {
          toast.error(formatApiError(err, "Failed to rename tier"));
          setName(tier.name);
          setEditing(false);
        },
      },
    );
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Badge must be an image");
      return;
    }
    if (file.size > BADGE_MAX_BYTES) {
      toast.error("Badge must be 1 MB or smaller");
      return;
    }
    uploadBadge.mutate(
      { programId, tierId: tier.id, file },
      {
        onSuccess: () => toast.success("Badge updated"),
        onError: (err) => toast.error(formatApiError(err, "Failed to upload badge")),
      },
    );
  }

  function handleDeleteClick() {
    if (assignedCount > 0) {
      setReassignOpen(true);
    } else {
      setDeleteOpen(true);
    }
  }

  function handleConfirmDelete() {
    deleteTier.mutate(
      { programId, id: tier.id },
      {
        onSuccess: () => {
          toast.success("Tier deleted");
          setDeleteOpen(false);
        },
        onError: (err) => {
          toast.error(formatApiError(err, "Failed to delete tier"));
          setDeleteOpen(false);
        },
      },
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "min-w-[160px] rounded-xl border bg-card p-3 transition-shadow",
        isDragging && "z-50 opacity-80 shadow-lg",
      )}
    >
      <div className="mb-2 flex items-center justify-center">
        <TierBadge
          assignment={{
            tierName: tier.name,
            badgePath: tier.badge_path,
            colorHex: tier.color_hex,
            legacyTier: tier.legacy_tier,
          }}
        />
      </div>

      {editing ? (
        <div className="flex items-center gap-1">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSaveName();
              if (e.key === "Escape") {
                setName(tier.name);
                setEditing(false);
              }
            }}
            onBlur={onSaveName}
            className="h-7 text-xs"
            autoFocus
            maxLength={80}
            disabled={updateTier.isPending}
          />
          <button
            type="button"
            onClick={onSaveName}
            className="text-emerald-600 hover:text-emerald-700"
            aria-label="Save"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              setName(tier.name);
              setEditing(false);
            }}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Cancel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="group/name flex w-full items-center justify-center gap-1 truncate text-center text-sm font-medium tracking-tight hover:text-accent"
        >
          <span className="truncate">{tier.name}</span>
          <Pencil className="h-3 w-3 text-muted-foreground/60 opacity-0 transition group-hover/name:opacity-100" />
        </button>
      )}

      {tier.legacy_tier && (
        <p className="mt-0.5 text-center font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {tier.legacy_tier}
        </p>
      )}

      <div className="mt-2 flex items-center justify-center gap-1">
        <button
          type="button"
          aria-label="Drag to reorder column"
          {...attributes}
          {...listeners}
          disabled={!canDrag}
          className="cursor-grab text-muted-foreground/60 transition hover:text-foreground active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadBadge.isPending}
          aria-label="Upload badge"
        >
          {uploadBadge.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-muted-foreground hover:text-destructive"
          onClick={handleDeleteClick}
          disabled={deleteTier.isPending}
          aria-label="Delete tier"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPickFile}
        />
      </div>

      <AlertDialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reassign partners first</AlertDialogTitle>
            <AlertDialogDescription>
              {assignedCount} partner{assignedCount === 1 ? " is" : "s are"}{" "}
              currently assigned to{" "}
              <span className="font-medium text-foreground">{tier.name}</span>.
              Move them to a different tier from the Assignments tab before
              deleting this column.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this tier?</AlertDialogTitle>
            <AlertDialogDescription>
              Cells in this column will be removed. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTier.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteTier.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteTier.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete tier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
